import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Shield, KeyRound } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import BrainLogo from "@/components/BrainLogo";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const { login, loginWith2fa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // "credentials" | "2fa"
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [errs, setErrs] = useState({});
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const onSubmit = async (e) => {
    e.preventDefault();
    // Read the LIVE values straight from the form. Browser autofill (Safari /
    // password managers) often sets the input value without firing React's
    // onChange, leaving state stale/empty — which looks like "correct password
    // rejected". Reading from the DOM guarantees we send what's actually shown.
    const form = e.currentTarget;
    const liveEmail = (form.elements.email?.value ?? email).trim();
    const livePassword = form.elements.password?.value ?? password;
    const v = {};
    if (!liveEmail) v.email = "Enter your email address.";
    else if (!EMAIL_RE.test(liveEmail)) v.email = "That doesn't look like a valid email (e.g. you@email.com).";
    if (!livePassword) v.password = "Enter your password.";
    setErrs(v);
    if (Object.keys(v).length) return;
    setLoading(true);
    try {
      const result = await login(liveEmail, livePassword, remember);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setStep("2fa");
        toast.info("Enter your 2FA code to continue.");
      } else {
        toast.success("Signed in.");
        nav(result.user?.role === "viewer" ? "/admin" : "/app");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't sign in. Check your email and password.";
      setErrs({ form: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const on2faSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) return;
    setLoading(true);
    try {
      await loginWith2fa(tempToken, otpCode.trim(), remember);
      toast.success("Signed in.");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
      setOtpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 mesh-bg">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px]"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-12" data-testid="login-logo-link">
          <BrainLogo size={32} />
          <span className="font-semibold tracking-tight text-[18px]">Studivault</span>
        </Link>

        <AnimatePresence mode="wait">
          {step === "credentials" ? (
            <motion.div key="creds" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <h1 className="display-3">Welcome back</h1>
                <p className="text-[15px] text-[#B0AEBC] mt-2">Sign in to continue studying.</p>
              </div>

              <form onSubmit={onSubmit} noValidate data-testid="login-form" className="space-y-3">
                <div>
                  <label htmlFor="login-email" className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Email</label>
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    required
                    aria-invalid={!!errs.email}
                    aria-describedby={errs.email ? "login-email-error" : undefined}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (errs.email || errs.form) setErrs((x) => ({ ...x, email: null, form: null })); }}
                    data-testid="login-email-input"
                    className="input"
                    placeholder="you@email.com"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {errs.email && <p id="login-email-error" role="alert" className="text-[12px] text-[#F87171] mt-1.5" data-testid="login-email-error">{errs.email}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="login-password" className="text-[12px] font-medium text-[#B0AEBC]">Password</label>
                    <Link to="/forgot-password" className="text-[12px] text-[#B8A4FF] hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    id="login-password"
                    type="password"
                    name="password"
                    required
                    aria-invalid={!!errs.password}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (errs.password || errs.form) setErrs((x) => ({ ...x, password: null, form: null })); }}
                    data-testid="login-password-input"
                    className="input"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  {errs.password && <p role="alert" className="text-[12px] text-[#F87171] mt-1.5" data-testid="login-password-error">{errs.password}</p>}
                  {errs.form && <p role="alert" className="text-[12px] text-[#F87171] mt-1.5" data-testid="login-form-error">{errs.form}</p>}
                </div>

                <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none" data-testid="keep-signed-in-label">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="keep-signed-in-checkbox"
                    className="w-4 h-4 rounded border-[#3a3a4e] bg-transparent accent-[#7C5CFF] cursor-pointer"
                  />
                  <span className="text-[13px] text-[#B0AEBC]">Keep me signed in</span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="btn btn-primary w-full mt-6 py-3.5 text-[15px]"
                >
                  {loading ? "Signing in…" : <>Sign in <ArrowRight size={15} className="inline ml-1 -mt-0.5" /></>}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[12px] text-[#8E8B99]">or</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <GoogleButton label="Sign in with Google" />

              <p className="text-center text-[14px] text-[#B0AEBC] mt-8">
                No account?{" "}
                <Link to="/signup" className="text-[#B8A4FF] hover:underline" data-testid="login-signup-link">
                  Sign up for free
                </Link>
              </p>
            </motion.div>
          ) : (
            <motion.div key="2fa" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="text-center mb-10">
                <div className="w-14 h-14 rounded-2xl bg-[#1a1a2e] border border-[#2a2a3e] flex items-center justify-center mx-auto mb-4">
                  <Shield size={26} className="text-[#B8A4FF]" />
                </div>
                <h1 className="display-3">Two-Factor Auth</h1>
                <p className="text-[15px] text-[#B0AEBC] mt-2">Open your authenticator app and enter the 6-digit code.</p>
              </div>

              <form onSubmit={on2faSubmit} data-testid="twofa-form" className="space-y-4">
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Authentication Code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="twofa-code-input"
                    className="input text-center text-2xl tracking-[0.4em] font-mono"
                    placeholder="000000"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="twofa-submit-button"
                  className="btn btn-primary w-full py-3.5 text-[15px]"
                >
                  {loading ? "Verifying…" : <>Verify <KeyRound size={14} className="inline ml-1 -mt-0.5" /></>}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setTempToken(""); setOtpCode(""); }}
                  className="w-full text-center text-[13px] text-[#B0AEBC] hover:text-white mt-2"
                >
                  ← Back to sign in
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
